import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from 'dayjs'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const EditOrganizationPage = () => {
  const { uuid } = useParams()
  const { setBreadcrumbs } = usePageTitle()
  const [organizationData, setOrganizationData] = useState({
    name: '',
    address: '',
    phone: '',
    startDate: dayjs(),
    active: true,
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setFetching(true)
        const { data } = await fetchWithAuth(`organizations/${uuid}`)
        setOrganizationData({
          name: data.name || '',
          address: data.address || '',
          phone: data.phone || '',
          startDate: data.startDate ? dayjs(data.startDate) : dayjs(),
          active: data.active !== undefined ? data.active : true,
        })
        setBreadcrumbs(['organizations', data.name, 'edit'])
      } catch (error) {
        console.error('Error fetching organization:', error)
        setError('Failed to load organization. Please try again.')
      } finally {
        setFetching(false)
      }
    }

    fetchOrganization()
    return () => setBreadcrumbs([])
  }, [uuid])

  const validate = () => {
    const newErrors = {}

    if (!organizationData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!organizationData.address.trim()) {
      newErrors.address = 'Address is required'
    }

    if (!organizationData.phone.trim()) {
      newErrors.phone = 'Phone is required'
    }

    if (!organizationData.startDate) {
      newErrors.startDate = 'Start Date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setOrganizationData({
      ...organizationData,
      [name]: type === 'checkbox' ? checked : value,
    })

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const handleDateChange = (newDate) => {
    setOrganizationData({
      ...organizationData,
      startDate: newDate,
    })

    if (errors.startDate) {
      setErrors({
        ...errors,
        startDate: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const formattedData = {
        ...organizationData,
        startDate: organizationData.startDate.toISOString(),
      }

      const response = await fetchWithAuth(`organizations/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      })

      if (response.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          navigate(`/organizations/${uuid}`)
        }, 1500)
      } else {
        const data = await response.json()
        if (data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else {
          setError(data.message || 'Failed to update organization')
        }
      }
    } catch (error) {
      console.error('Error updating organization:', error)
      setError('Failed to update organization. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading organization...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to={`/organizations/${uuid}`} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Edit Organization
        </Typography>
      </Stack>
      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Activity mode={success ? 'visible' : 'hidden'}>
        <Alert severity="success" sx={{ mb: 3 }}>
          Organization updated successfully! Redirecting...
        </Alert>
      </Activity>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={organizationData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              label="Address"
              name="address"
              value={organizationData.address}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.address}
              helperText={errors.address}
            />

            <TextField
              label="Phone"
              name="phone"
              value={organizationData.phone}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.phone}
              helperText={errors.phone}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={organizationData.startDate}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate,
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel id="status-label" htmlFor="status">
                Status
              </InputLabel>
              <Select
                labelId="status-label"
                name="active"
                value={organizationData.active}
                onChange={handleChange}
                label="Status"
                inputProps={{ id: 'status' }}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to={`/organizations/${uuid}`}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default EditOrganizationPage
